import type { MockProduct } from "./theme";
import { mockProducts } from "./theme";

export interface CouponRule {
  code: string;
  type: "percent" | "fixed" | "free_shipping";
  value: number; // percentage (e.g. 10) or fixed amount in INR (e.g. 150)
  minOrderValue: number;
  maxDiscount?: number;
  description: string;
  expiresAt?: string;
}

export interface ShippingMethod {
  id: string;
  title: string;
  carrier: string;
  rate: number;
  estimatedDays: string;
  freeAboveThreshold?: number;
  description: string;
}

export interface PaymentOption {
  id: "prepaid" | "partial_cod";
  title: string;
  badge?: string;
  description: string;
  discountPercent?: number; // e.g. 5 for 5% off
  upfrontPercent?: number; // e.g. 5 for 5% upfront payment
}

export const commerceConfig = {
  currency: {
    code: "INR",
    symbol: "₹",
    locale: "en-IN",
  },

  // Free shipping threshold in ₹ INR (Synced with backend dynamic rules)
  shipping: {
    freeShippingThreshold: 499,
    standardRate: 40,
    // Only ONE shipping method is offered to customers
    methods: [
      {
        id: "standard_insured",
        title: "Standard Insured Delivery",
        carrier: "Insured Express Logistics",
        rate: 40,
        estimatedDays: "2 – 4 Business Days",
        freeAboveThreshold: 499,
        description: "Carbon-neutral eco packaging with comprehensive transit insurance across all serviceable PIN codes.",
      },
    ] as ShippingMethod[],
  },

  // Payment Options
  paymentOptions: [
    {
      id: "prepaid",
      title: "Online Payment (Prepaid)",
      badge: "Instant 5% OFF",
      description: "Pay online via UPI, Credit/Debit Card, NetBanking, or Wallet to receive an instant 5% discount on your order subtotal.",
      discountPercent: 5,
    },
    {
      id: "partial_cod",
      title: "Partial Cash on Delivery",
      badge: "5% Advance",
      description: "Pay 5% upfront online to confirm your order. The remaining 95% balance is payable at your doorstep via Cash or UPI.",
      upfrontPercent: 5,
    },
  ] as PaymentOption[],

  // Configured coupon rules
  coupons: [
    {
      code: "MEOORA10",
      type: "percent",
      value: 10,
      minOrderValue: 499,
      maxDiscount: 300,
      description: "10% off your entire order (Min. order ₹499)",
    },
    {
      code: "WELCOME15",
      type: "percent",
      value: 15,
      minOrderValue: 999,
      maxDiscount: 500,
      description: "15% off welcome offer for clean beauty explorers (Min. order ₹999)",
    },
    {
      code: "FREESHIP",
      type: "free_shipping",
      value: 0,
      minOrderValue: 499,
      description: "Complimentary shipping on orders above ₹499",
    },
    {
      code: "GLOW20",
      type: "percent",
      value: 20,
      minOrderValue: 1999,
      maxDiscount: 800,
      description: "20% off festival special on orders above ₹1,999",
    },
  ] as CouponRule[],

  // Payment Gateway Configuration
  razorpay: {
    merchantName: "Meoora Botanicals",
    themeColor: "#34442b",
    defaultKeyId: process.env.GATSBY_RAZORPAY_KEY_ID || "rzp_test_TS77YwrHHm6QfA",
  },

  // Backend API URL for Headless Auth and Order Sync
  apiUrl: process.env.GATSBY_API_URL || "https://admin.meoora.in/wp-json/meoora/v1",
};

/**
 * Calculates discount for a given coupon and subtotal
 */
export function validateAndApplyCoupon(
  code: string,
  subtotal: number,
  currentShippingFee: number
): { valid: boolean; discount: number; shippingDiscount: number; message: string; rule?: CouponRule } {
  const normalized = code.trim().toUpperCase();
  const rule = commerceConfig.coupons.find((c) => c.code === normalized);

  if (!rule) {
    return {
      valid: false,
      discount: 0,
      shippingDiscount: 0,
      message: `Coupon code "${normalized}" is not recognized.`,
    };
  }

  if (subtotal < rule.minOrderValue) {
    return {
      valid: false,
      discount: 0,
      shippingDiscount: 0,
      message: `Coupon "${normalized}" requires a minimum order value of ${commerceConfig.currency.symbol}${rule.minOrderValue.toLocaleString(commerceConfig.currency.locale)}.`,
    };
  }

  let discount = 0;
  let shippingDiscount = 0;

  if (rule.type === "percent") {
    discount = Math.round((subtotal * rule.value) / 100);
    if (rule.maxDiscount && discount > rule.maxDiscount) {
      discount = rule.maxDiscount;
    }
  } else if (rule.type === "fixed") {
    discount = Math.min(rule.value, subtotal);
  } else if (rule.type === "free_shipping") {
    shippingDiscount = currentShippingFee;
  }

  return {
    valid: true,
    discount,
    shippingDiscount,
    message: `Coupon "${normalized}" applied successfully!`,
    rule,
  };
}

/**
 * Dynamically validates a coupon against the live WooCommerce backend engine.
 * Falls back to client-side rules if network is offline or server unreachable.
 */
export async function validateCouponOnBackend(
  code: string,
  subtotal: number,
  currentShippingFee = 0
): Promise<{
  valid: boolean;
  discount: number;
  shippingDiscount: number;
  freeShipping: boolean;
  message: string;
  rule?: CouponRule;
}> {
  const normalized = code.trim().toUpperCase();
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/coupons/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: normalized,
        subtotal,
        shipping: currentShippingFee,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && data.valid) {
        const rule: CouponRule = {
          code: data.code.toUpperCase(),
          type: data.freeShipping ? "free_shipping" : data.discountType === "percent" ? "percent" : "fixed",
          value: Number(data.discountAmount) || 0,
          minOrderValue: 0,
          description: data.description || "Active promotion applied",
        };
        return {
          valid: true,
          discount: Number(data.discountAmount) || 0,
          shippingDiscount: Number(data.shippingDiscount) || 0,
          freeShipping: Boolean(data.freeShipping),
          message: data.message || `Coupon "${normalized}" applied successfully!`,
          rule,
        };
      } else if (data.message) {
        return {
          valid: false,
          discount: 0,
          shippingDiscount: 0,
          freeShipping: false,
          message: data.message,
        };
      }
    }
  } catch (err) {
    console.warn("Live coupon backend check failed, falling back to local validator:", err);
  }

  // Graceful offline fallback
  const fallback = validateAndApplyCoupon(normalized, subtotal, currentShippingFee);
  return {
    valid: fallback.valid,
    discount: fallback.discount,
    shippingDiscount: fallback.shippingDiscount,
    freeShipping: fallback.rule?.type === "free_shipping",
    message: fallback.message,
    rule: fallback.rule,
  };
}

export interface DynamicShippingConfig {
  freeShippingThreshold: number;
  standardRate: number;
  currency: string;
  currencySymbol: string;
}

export interface OrderTrackingMilestone {
  id: string;
  title: string;
  description: string;
  time: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

export interface OrderTrackingInfo {
  orderId: string;
  databaseId: number;
  awb: string;
  courierName: string;
  shipmentId: string;
  trackingUrl: string;
  shiprocketStatus: string;
  currentStage: number; // 1: Confirmed -> 2: Packed -> 3: Handed to Courier -> 4: In Transit -> 5: Out for Delivery -> 6: Delivered
  statusLabel: string;
  estimatedDelivery: string;
  paymentOption: "prepaid" | "partial_cod";
  upfrontPaid: number;
  balanceDue: number;
  codAmountToCollect: number;
  milestones: OrderTrackingMilestone[];
}

export interface StorefrontFaq {
  question: string;
  answer: string;
}

export interface StorefrontCountdown {
  heading: string;
  subheading: string;
  buttonText: string;
  buttonLink: string;
}

export interface StorefrontConfig {
  announcements: string[];
  promoBar: {
    enabled: boolean;
    text: string;
    code: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    buttonText: string;
    buttonLink: string;
  };
  faqs?: StorefrontFaq[];
  countdown?: StorefrontCountdown;
}

export interface FeaturedReview {
  id: number | string;
  name: string;
  stars: number;
  title: string;
  quote: string;
  date: string;
  verified: boolean;
  product: {
    id: number | string;
    title: string;
    slug: string;
    handle: string;
    image: string;
  };
}

/**
 * Dynamically fetches the backend-configured shipping rules from WordPress REST API.
 * Falls back to default commerceConfig settings if offline or unreachable.
 */
export async function fetchShippingConfig(): Promise<DynamicShippingConfig> {
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/config/shipping`);
    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        return {
          freeShippingThreshold: Number(data.freeShippingThreshold) || 499,
          standardRate: Number(data.standardRate) || 40,
          currency: data.currency || "INR",
          currencySymbol: data.currencySymbol || "₹",
        };
      }
    }
  } catch (err) {
    console.warn("Could not retrieve backend shipping rules, utilizing default fallback:", err);
  }
  return {
    freeShippingThreshold: commerceConfig.shipping.freeShippingThreshold,
    standardRate: commerceConfig.shipping.standardRate,
    currency: "INR",
    currencySymbol: "₹",
  };
}

/**
 * Dynamically fetches storefront controls (announcements, promo bar, hero, FAQs, countdown) from WordPress REST API.
 */
export async function fetchStorefrontConfig(): Promise<StorefrontConfig | null> {
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/config/storefront`);
    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        return {
          announcements: data.announcements || [],
          promoBar: data.promoBar || { enabled: false, text: "", code: "" },
          hero: data.hero || {
            eyebrow: "Clean Botanical Formulations",
            title: "The Art of Natural Beauty",
            subtitle: "Sustainably wild-harvested botanicals and clinical actives.",
            buttonText: "Explore Formulations",
            buttonLink: "/collections/all",
          },
          faqs: Array.isArray(data.faqs) ? data.faqs : [],
          countdown: data.countdown || undefined,
        };
      }
    }
  } catch (err) {
    console.warn("Could not retrieve storefront config, utilizing default fallback:", err);
  }
  return null;
}

let cachedProducts: MockProduct[] | null = null;

/**
 * Fetches all authentic WooCommerce catalog products from the WordPress REST API.
 * Uses client-side caching for instant responsiveness while ensuring live data truth.
 */
export async function fetchCatalogProducts(forceRefresh = false): Promise<MockProduct[]> {
  if (cachedProducts && !forceRefresh) {
    return cachedProducts;
  }
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/catalog/products`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.products) && data.products.length > 0) {
        cachedProducts = data.products as MockProduct[];
        return cachedProducts;
      }
    }
  } catch (err) {
    console.warn("Could not retrieve live catalog from backend, falling back to bundled catalog:", err);
  }
  return mockProducts;
}

/**
 * Fetches approved, authentic customer reviews from the WooCommerce database.
 */
export async function fetchFeaturedReviews(): Promise<FeaturedReview[]> {
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/reviews/featured`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.reviews) && data.reviews.length > 0) {
        return data.reviews as FeaturedReview[];
      }
    }
  } catch (err) {
    console.warn("Could not retrieve featured reviews from backend:", err);
  }
  return [];
}

/**
 * Fetches real, live order tracking info (Shiprocket compatible) for a given order ID.
 */
export async function fetchOrderTracking(orderId: string | number): Promise<OrderTrackingInfo | null> {
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/orders/${orderId}/tracking`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.tracking) {
        return data.tracking as OrderTrackingInfo;
      }
    }
  } catch (err) {
    console.warn(`Could not retrieve tracking for order #${orderId}:`, err);
  }
  return null;
}

export interface BackendCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
  description: string;
  image?: string;
}

let cachedCategories: BackendCategory[] | null = null;

/**
 * Dynamically fetches active WooCommerce product categories from WordPress REST API.
 */
export async function fetchCatalogCategories(forceRefresh = false): Promise<BackendCategory[]> {
  if (cachedCategories && !forceRefresh) {
    return cachedCategories;
  }
  try {
    const res = await fetch(`${commerceConfig.apiUrl}/catalog/categories`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.categories)) {
        cachedCategories = data.categories as BackendCategory[];
        return cachedCategories;
      }
    }
  } catch (err) {
    console.warn("Could not retrieve backend categories, using default categories:", err);
  }
  return [
    { id: 169, name: "Skincare", slug: "skincare", count: 6, description: "Potent botanical formulations" },
    { id: 172, name: "Serums & Treatments", slug: "serums", count: 2, description: "Concentrated active elixirs" },
    { id: 178, name: "Moisturizers & Creams", slug: "moisturizers", count: 1, description: "Deep barrier creams" },
    { id: 181, name: "Botanical Oils", slug: "oils", count: 1, description: "Pure cold-pressed facial oils" },
    { id: 184, name: "Clean Makeup", slug: "makeup", count: 3, description: "Mineral & plant pigment cosmetics" },
    { id: 187, name: "Haircare", slug: "haircare", count: 2, description: "Silicone-free hair elixirs" },
    { id: 190, name: "Bodycare", slug: "bodycare", count: 1, description: "Nourishing botanical body rituals" },
  ];
}

